

import { useEffect, useState ,useRef} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";

import {
  getMedicineTypeAction,
  deleteMedicineTypeAction,
} from "../../../store/actions/MedicineTypeActions";

const MedicineType = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstLoad = useRef(true);

  const list = useSelector((state) => state.medicineType.list);
  const numRows = useSelector((state) => state.medicineType.num_rows || 0);
  let searchTimeout = null;

  const [searchQueries, setSearchQueries] = useState({
    medicine_type_text: "",
    medicine_type_status: "",
  });

  const [sortField, setSortField] = useState("medicine_type_text");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  // ================= FETCH =================
  const fetchMedicineTypeFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ show loader only on first visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getMedicineTypeAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch medicine type:", err);
      setError("Failed to load medicine type data.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever after first load
    }
  };
  

  useEffect(() => {
    // Check if returning from edit page with state
    if (location?.state?.returnTo) {
      const { currentPage: prevPage, searchQueries: prevSearches, sortField: prevSort, sortOrder: prevOrder } = location.state.returnTo;
      setCurrentPage(prevPage || 0);
      setSearchQueries(prevSearches || {});
      setSortField(prevSort || "medicine_type_text");
      setSortOrder(prevOrder || "asc");

      // Determine the search field and keyword from restored state
      const searchField = prevSearches?.medicine_type_text ? "medicine_type_text" : prevSearches?.medicine_type_status ? "medicine_type_status" : "";
      const searchKeyword = prevSearches?.medicine_type_text || prevSearches?.medicine_type_status || "";

      setActiveSearchField(searchField);
      setActiveSearchKeyword(searchKeyword);

      // Fetch with restored state
      fetchMedicineTypeFromServer(
        searchField,
        searchKeyword,
        true,
        prevPage || 0,
        prevSort || "medicine_type_text",
        prevOrder || "asc"
      );
    } else {
      fetchMedicineTypeFromServer();
    }
  }, []);

  // ================= SEARCH =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
  
    const clearedQueries = {
      medicine_type_text: "",
      medicine_type_status: "",
    };
  
    const updatedQueries = { ...clearedQueries, [field]: keyword };
  
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
  
    // ✅ debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchMedicineTypeFromServer(
        field,
        keyword,
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400);
  };
  
  // ================= SORT =================
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
    fetchMedicineTypeFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order);
  };

  // ================= PAGINATION =================
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchMedicineTypeFromServer(activeSearchField, activeSearchKeyword, true, page, sortField, sortOrder);
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this item!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
  
          await dispatch(deleteMedicineTypeAction(id));
  
          // Refresh list after delete
          await fetchMedicineTypeFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete medicine type");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };
  

  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="text-end mb-3">
        <Link
          to="/add-medicine-type"
          state={{
            returnTo: {
              currentPage,
              searchQueries,
              sortField,
              sortOrder,
            },
          }}
          className="btn btn-primary"
        >
          + Add Medicine Type
        </Link>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="table-light">
                  <tr>
                    {/* <th>#</th> */}
                    <th onClick={() => handleSort("medicine_type_text")} style={{ cursor: "pointer" }}>
                      Medicine Type {sortField === "medicine_type_text" && (sortOrder === "asc" ? "↑" : "↓")}
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Search medicine type"
                        value={searchQueries.medicine_type_text}
                        onChange={(e) => handleSearch(e, "medicine_type_text")}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th onClick={() => handleSort("medicine_type_status")} style={{ cursor: "pointer" }}>
                      Status {sortField === "medicine_type_status" && (sortOrder === "asc" ? "↑" : "↓")}
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Search status"
                        value={searchQueries.medicine_type_status}
                        onChange={(e) => handleSearch(e, "medicine_type_status")}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {list?.length > 0 ? (
                    list.map((item, index) => (
                      <tr key={item.medicine_type_id}>
                        {/* <td>{currentPage * rowsPerPage + index + 1}</td> */}
                        <td>{item.medicine_type_text}</td>
                        <td>
                          <span
                            className={`badge px-3 py-1 rounded-pill text-white ${
                              item.medicine_type_status === "Active"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                          >
                            {item.medicine_type_status}
                          </span>
                        </td>

                        <td>
                          <div className="d-flex">
                            <button
                              className="btn btn-link p-0 me-3"
                              onClick={() =>
                                navigate("/add-medicine-type", {
                                  state: {
                                    item: {
                                      medicine_type_id: item.medicine_type_id,
                                      medicine_type_text: item.medicine_type_text,
                                      medicine_type_status: item.medicine_type_status,
                                    },
                                    returnTo: {
                                      currentPage,
                                      searchQueries,
                                      sortField,
                                      sortOrder,
                                    },
                                  },
                                })
                              }
                              
                            >
                              <i className="fa fa-pencil-alt text-success"></i>
                            </button>

                            <button
                              className="btn btn-link p-0"
                              onClick={() =>
                                handleDelete(item.medicine_type_id)
                              }
                            >
                              <i className="fa fa-trash text-danger"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No Medicine Type Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              className="pagination-wrapper"
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <div>
                Showing {list?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                  disabled={currentPage === 0}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const startPage = Math.max(
                    0,
                    Math.min(currentPage - 2, totalPages - maxPagesToShow)
                  );
                  const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                  if (startPage > 0) {
                    pages.push(
                      <button
                        key={0}
                        onClick={() => handlePageChange(0)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        1
                      </button>
                    );
                    if (startPage > 1) {
                      pages.push(
                        <span
                          key="dots1"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i < endPage; i++) {
                    const isActive = currentPage === i;
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: isActive ? "#007bff" : "#fff",
                          color: isActive ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span
                          key="dots2"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages - 1}
                        onClick={() => handlePageChange(totalPages - 1)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                  disabled={currentPage === totalPages - 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MedicineType;
